import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '../stores/auth.store';
import { useWorkforceStore } from '../stores/workforce.store';
import { useToast } from '../lib/toast';
import { roundTo5 } from '../pages/workforce/wfShared';

const toMin = (t) => {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + m;
};

/**
 * Shared hook for the current user's live shift session (`/workforce/presenze/sessione`).
 *
 * Purpose:
 *   Provides a single reactive source of truth for both `PunchClock` (Home) and
 *   `PresenceStatusBadge` (TopBar), avoiding duplicate network calls and keeping
 *   all presence indicators synchronized across views and browser tabs.
 *
 * Inputs:
 *   None (reads the authenticated user from `useAuthStore` and session state from `useWorkforceStore`).
 *
 * Outputs:
 *   `{ session, loading, busy, inServizio, inPausa, statusTone, statusText, shortStatusText, clockIn, togglePausa, clockOut, refresh }`
 *
 * Side effects:
 *   Fetches `/workforce/presenze/sessione` on mount, window focus, tab visibility change,
 *   and `wfm-presence-session-changed` events; mutates server session via `useWorkforceStore`.
 */
export function usePresenceSession() {
  const me = useAuthStore((s) => s.user);
  const session = useWorkforceStore((s) => s.miaSessione);
  const pronta = useWorkforceStore((s) => s.miaSessionePronta);
  const getSessione = useWorkforceStore((s) => s.getSessione);
  const sessioneEntrata = useWorkforceStore((s) => s.sessioneEntrata);
  const sessionePausa = useWorkforceStore((s) => s.sessionePausa);
  const sessioneUscita = useWorkforceStore((s) => s.sessioneUscita);
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!me?.id) return null;
    try {
      return await getSessione();
    } catch (_) {
      return null;
    }
  }, [me?.id, getSessione]);

  useEffect(() => {
    if (!me?.id) return undefined;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const onSync = () => {
      refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    window.addEventListener('wfm-presence-session-changed', onSync);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('wfm-presence-session-changed', onSync);
    };
  }, [me?.id, refresh]);

  const clockIn = useCallback(async () => {
    if (!me?.id || session || busy) return false;
    const p = roundTo5();
    setBusy(true);
    try {
      await sessioneEntrata({ data: p.data, ora_inizio: p.ora });
      toast.success(`Entrata registrata alle ${p.ora}`);
      return true;
    } catch (e) {
      toast.error(e.message || "Errore nella registrazione dell'entrata");
      return false;
    } finally {
      setBusy(false);
    }
  }, [me?.id, session, busy, sessioneEntrata, toast]);

  const togglePausa = useCallback(async () => {
    if (!session || busy) return false;
    const { ora } = roundTo5();
    setBusy(true);
    try {
      const s = await sessionePausa(ora);
      toast.success(s.inPausa ? `In pausa dalle ${ora}` : 'Pausa terminata, bentornato');
      return true;
    } catch (e) {
      toast.error(e.message || 'Errore nella gestione della pausa');
      return false;
    } finally {
      setBusy(false);
    }
  }, [session, busy, sessionePausa, toast]);

  const clockOut = useCallback(async () => {
    if (!session || busy) return false;
    const out = roundTo5();
    if (out.data !== String(session.data).slice(0, 10)) {
      toast.error('Timbratura a cavallo della mezzanotte: registra la presenza manualmente.');
      return false;
    }
    if (toMin(out.ora) <= toMin(session.oraInizio)) {
      toast.error(
        "Sei in servizio da pochi minuti: registra l'uscita più tardi (gli orari sono arrotondati a 5 minuti)."
      );
      return false;
    }
    setBusy(true);
    try {
      const reg = await sessioneUscita({ data: out.data, ora_fine: out.ora });
      toast.success(`Uscita registrata: ${session.oraInizio}-${out.ora}`);
      // FEAT-05: a few minutes over the schedule are not an overtime request.
      if (reg?.straordinariStato === 'sotto_soglia') {
        toast.info(
          `Hai lavorato ${reg.straordinariMinuti} minuti in più: sotto la soglia di ${reg.soglia_straordinari_minuti} minuti non viene richiesto lo straordinario.`
        );
      }
      return true;
    } catch (e) {
      toast.error(e.message || "Errore nella registrazione dell'uscita");
      return false;
    } finally {
      setBusy(false);
    }
  }, [session, busy, sessioneUscita, toast]);

  const loading = !pronta;
  const inServizio = !!session;
  const inPausa = !!session && !!session.inPausa;

  const statusTone = inPausa
    ? {
        bg: 'var(--wfm-warning-bg)',
        text: 'var(--wfm-warning-text)',
        border: 'var(--wfm-warning-border)',
        dot: 'var(--wfm-warning)',
      }
    : inServizio
      ? {
          bg: 'var(--wfm-success-bg)',
          text: 'var(--wfm-success-text)',
          border: 'var(--wfm-success-border)',
          dot: 'var(--wfm-success)',
        }
      : {
          bg: 'var(--wfm-bg-light)',
          text: 'var(--wfm-text-mid)',
          border: 'var(--wfm-border)',
          dot: 'var(--wfm-text-muted)',
        };

  const statusText = loading
    ? 'Verifica sessione...'
    : inPausa
      ? `In pausa dalle ${session.pausaInizio || ''}`
      : inServizio
        ? `In servizio da ${session.oraInizio}`
        : 'Fuori servizio';

  const shortStatusText = loading
    ? 'Turno...'
    : inPausa
      ? `In pausa${session.pausaInizio ? ` · ${session.pausaInizio}` : ''}`
      : inServizio
        ? `In servizio${session.oraInizio ? ` · ${session.oraInizio}` : ''}`
        : 'Fuori servizio';

  return {
    session,
    loading,
    busy,
    inServizio,
    inPausa,
    statusTone,
    statusText,
    shortStatusText,
    clockIn,
    togglePausa,
    clockOut,
    refresh,
  };
}
